import { useEffect, useRef, useState } from 'react';
import { Share2, Link as LinkIcon, MessageCircle } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { isKakaoAvailable, loadKakao } from '@/lib/kakao';

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string;
  /** 버튼 variant (Button 컴포넌트 기본 prop). 기본 'secondary' */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  className?: string;
}

/**
 * 공유하기 버튼.
 * - 모바일 (navigator.share 지원): 네이티브 공유 시트 사용
 * - PC: 드롭다운 메뉴 (링크 복사 / 카카오톡 공유)
 * - Kakao SDK 가 사용 불가 (VITE_KAKAO_JS_KEY 미설정) 하면 카카오 항목 숨김
 */
export function ShareButton({
  url,
  title,
  description,
  imageUrl,
  variant = 'secondary',
  className,
}: ShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toastTimerRef = useRef<number | null>(null);

  const hasNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const kakaoAvailable = isKakaoAvailable();

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 1800);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const handleCopyLink = async () => {
    setOpen(false);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        /* 구형 브라우저 fallback */
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast('링크가 복사됐어요');
    } catch {
      showToast('복사에 실패했어요');
    }
  };

  const handleKakaoShare = async () => {
    setOpen(false);
    const Kakao = await loadKakao();
    if (!Kakao?.Share?.sendDefault) {
      showToast('카카오 공유를 사용할 수 없어요');
      return;
    }
    try {
      Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title,
          description: description ?? '',
          imageUrl:
            imageUrl ?? 'https://community.insidemation.com/og_1200_630.png',
          link: { mobileWebUrl: url, webUrl: url },
        },
        buttons: [
          {
            title: '시세공간에서 보기',
            link: { mobileWebUrl: url, webUrl: url },
          },
        ],
      });
    } catch {
      showToast('카카오 공유에 실패했어요');
    }
  };

  const handleClick = async () => {
    /* 모바일은 네이티브 공유 시트 우선 */
    if (hasNativeShare) {
      try {
        await navigator.share({ title, text: description, url });
      } catch {
        /* 사용자 취소 등 — 무시 */
      }
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className ?? ''}`}>
      <Button variant={variant} onClick={handleClick} aria-label="공유하기">
        <Share2 className="h-4 w-4" />
        공유
      </Button>

      {/* PC 드롭다운 */}
      {open && !hasNativeShare && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-40 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg"
        >
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-bg)]"
          >
            <LinkIcon className="h-4 w-4" />
            링크 복사
          </button>
          {kakaoAvailable && (
            <button
              type="button"
              onClick={handleKakaoShare}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-bg)]"
            >
              <MessageCircle className="h-4 w-4 text-[#FEE500]" />
              카카오톡 공유
            </button>
          )}
        </div>
      )}

      {/* 간단 토스트 */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed left-1/2 bottom-20 z-50 -translate-x-1/2 rounded-full bg-[var(--color-text)] px-4 py-2 text-xs font-medium text-white shadow-lg"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
